import { useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Chapter } from "../components/Chapter";
import { Reveal, MaskedLine } from "../lib/motion";
import { usePageSeo } from "../lib/i18nContext";
import LocalizedLink from "../components/LocalizedLink";
import { StructuredLines } from "../components/Motifs";
import { MARKET_SLUGS } from "../i18n/markets";

const WRAP = "max-w-[1240px] mx-auto px-6 md:px-12 xl:px-16";
const H2 = "font-display text-[26px] md:text-[32px] font-semibold tracking-tight text-ink leading-tight";

const Market = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  usePageSeo(`markets.${slug}`);
  if (!MARKET_SLUGS.includes(slug)) return <Navigate to="/" replace />;

  const m = t(`markets.${slug}`, { returnObjects: true });
  const allCategories = t("categories.items", { returnObjects: true });
  const explore = (m?.explore?.ids || []).map((id) => allCategories.find((c) => c.id === id)).filter(Boolean);
  const dest = `/contact?destination=${encodeURIComponent(m?.name || "")}`;

  return (
    <main className="pt-nav">
      <section data-testid="market-hero" className="bg-white border-b border-ice relative overflow-hidden">
        <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-10 py-16 md:py-24`}>
          <div className="lg:col-span-8">
            <p className="font-mono text-xs md:text-[13px] font-medium tracking-[0.18em] uppercase text-pine mb-8" data-testid="market-eyebrow">{m.eyebrow}</p>
            <h1 className="font-display font-semibold text-ink text-[36px] md:text-[44px] tracking-tight leading-[1.12] max-w-4xl" data-testid="market-headline">
              <MaskedLine delay={0.1}>{m.line1}</MaskedLine>
              <MaskedLine delay={0.2}>{m.line2}</MaskedLine>
            </h1>
            <Reveal delay={0.3}>
              <p className="text-slate leading-relaxed mt-8 max-w-2xl text-base md:text-lg" data-testid="market-intro">{m.intro}</p>
            </Reveal>
            <Reveal delay={0.4}>
              <LocalizedLink to={dest} data-testid="market-hero-cta" className="inline-flex items-center gap-2 mt-10 bg-ink text-white font-semibold px-7 py-3.5 rounded text-sm transition-colors duration-300 hover:bg-deep">
                {m.cta.button} <ArrowUpRight size={16} className="rtl:-scale-x-100" />
              </LocalizedLink>
            </Reveal>
          </div>
          <div className="lg:col-span-4 hidden lg:flex items-center justify-end" aria-hidden="true">
            <StructuredLines cols={5} rows={5} className="w-56" />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-white">
        <div className={WRAP}>
          <Chapter index="01" label={m.explore.chapter} />
          <Reveal>
            <h2 className={`${H2} mt-8 max-w-3xl`} data-testid="market-explore-heading">{m.explore.heading}</h2>
          </Reveal>
          <div className="mt-12 md:mt-16 border-t border-ice">
            {explore.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.05}>
                <LocalizedLink to={`/products#${c.id}`} data-testid={`market-category-${c.id}`} className="group grid grid-cols-12 gap-4 items-baseline py-6 border-b border-ice">
                  <span className="col-span-2 md:col-span-1 font-mono text-xs text-pine">0{i + 1}</span>
                  <span className="col-span-10 md:col-span-4 font-display text-lg md:text-xl font-semibold text-ink group-hover:text-deep transition-colors">{c.name}</span>
                  <span className="col-span-12 md:col-span-5 text-sm text-slate leading-relaxed">{c.desc}</span>
                  <span className="col-span-12 md:col-span-2 inline-flex items-center md:justify-end gap-2 text-sm font-semibold text-ink">
                    {m.explore.link} <ArrowRight size={14} className="text-pine transition-transform duration-300 motion-safe:group-hover:translate-x-1 rtl:-scale-x-100" />
                  </span>
                </LocalizedLink>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-ice">
        <div className={WRAP}>
          <Chapter index="02" label={m.regulatory.chapter} />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-8">
            <Reveal className="lg:col-span-4">
              <h2 className={H2}>{m.regulatory.heading}</h2>
            </Reveal>
            <div className="lg:col-span-8">
              <div className="border-t border-ink/10 bg-white">
                {m.regulatory.items.map((item, i) => (
                  <Reveal key={item} delay={i * 0.05}>
                    <div className="flex items-start gap-5 px-6 md:px-8 py-6 border-b border-s border-e border-ink/10" data-testid={`market-reg-${i}`}>
                      <span className="font-mono text-xs text-pine w-8 shrink-0 mt-1">{String(i + 1).padStart(2, "0")}</span>
                      <p className="text-ink/85 leading-relaxed">{item}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <p className="text-sm text-ink/80 leading-relaxed mt-6 max-w-2xl">{m.regulatory.note}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-28 bg-white">
        <div className={WRAP}>
          <Chapter index="03" label={m.support.chapter} />
          <Reveal>
            <h2 className={`${H2} mt-8 max-w-3xl`}>{m.support.heading}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-s border-ice mt-14 md:mt-20">
            {m.support.items.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="border-e border-b border-ice p-8 md:p-10 h-full" data-testid={`market-support-${i}`}>
                  <p className="font-mono text-xs font-medium tracking-[0.18em] text-pine">0{i + 1}</p>
                  <h3 className="font-display text-xl font-semibold text-ink mt-6">{s.title}</h3>
                  <p className="text-sm text-slate mt-3 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <LocalizedLink to="/how-we-work" data-testid="market-how-cta" className="inline-flex items-center gap-2 mt-12 text-sm font-semibold text-ink underline decoration-pine decoration-2 underline-offset-[6px] hover:text-pine transition-colors">
            {m.support.cta} <ArrowRight size={16} className="rtl:-scale-x-100" />
          </LocalizedLink>
        </div>
      </section>

      <section className="on-navy bg-ink text-white">
        <div className={`${WRAP} py-16 md:py-24`}>
          <Reveal>
            <h2 className="font-display text-[26px] md:text-[32px] font-semibold tracking-tight leading-tight max-w-3xl" data-testid="market-cta-heading">{m.cta.heading}</h2>
            <p className="text-ice leading-relaxed mt-6 max-w-xl">{m.cta.text}</p>
            <LocalizedLink to={dest} data-testid="market-cta-button" className="inline-flex items-center gap-2 mt-10 bg-white text-ink font-semibold px-8 py-4 rounded text-sm transition-colors duration-300 hover:bg-ice">
              {m.cta.button} <ArrowUpRight size={16} className="rtl:-scale-x-100" />
            </LocalizedLink>
          </Reveal>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-14 pt-8 border-t border-white/15">
            <span className="font-mono text-xs tracking-[0.18em] uppercase text-ice/80 me-2">{m.related}</span>
            {MARKET_SLUGS.filter((s) => s !== slug).map((s) => (
              <LocalizedLink key={s} to={`/markets/${s}`} data-testid={`market-related-${s}`} className="text-sm text-white hover:text-ice underline decoration-white/30 underline-offset-4 transition-colors">
                {t(`markets.${s}.name`)}
              </LocalizedLink>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Market;
